/**
 * Module-level pub-sub store for chat panel tab switching.
 * Avoids Context Provider overhead — components subscribe via useAppTab().
 */
import { useState, useEffect } from "react";

export type AppTab = "chat" | "journal" | "settings";

type Listener = (tab: AppTab) => void;

let _currentTab: AppTab = "chat";
const _listeners = new Set<Listener>();

export function setAppTab(tab: AppTab): void {
  _currentTab = tab;
  _listeners.forEach((fn) => fn(tab));
}

export function getAppTab(): AppTab {
  return _currentTab;
}

export function useAppTab(): [AppTab, (tab: AppTab) => void] {
  const [tab, setTab] = useState<AppTab>(_currentTab);

  useEffect(() => {
    const handler = (t: AppTab) => setTab(t);
    _listeners.add(handler);
    // Sync in case store changed before effect ran
    setTab(_currentTab);
    return () => { _listeners.delete(handler); };
  }, []);

  return [tab, setAppTab];
}
